import { useState } from 'react';
import { Button, Dialog, Field, Icon, Input } from '@dayon/design-system';
import {
  AUDIT_REASON_MIN_LENGTH,
  IDENTITY_TRIES,
  ONE_MORE_TRY_REASON_TOO_SHORT,
  identityRetryGrantRequest,
  identityTriesLeft,
  type IdentityAllowedTry,
} from '@dayon/contracts';
import { momentWords } from './record-words.ts';

/**
 * SUPPORT ALLOWS A REFUSED PLAYER ONE MORE TRY — the owner, 23 Oct 2026: "3, support adds 1", with a
 * logged reason.
 *
 * A refused player whose reason was final, or whose three tries are spent, cannot send documents
 * again on their own. From their closed case, a member of staff holding kyc.review can allow one more
 * try: the player's identity page opens the form again, the new check goes to a person, and a refusal
 * of it closes the case again unless the rule leaves the player tries of their own. The reason goes
 * in the audit trail with the name of whoever gave it.
 *
 * The case route says where the press would be taken (`oneMoreTry.canAllow`), why not where it
 * would not (`oneMoreTry.whyNot`), and who allowed one more try after a refusal
 * (`oneMoreTry.allowed`); this file holds the dialog, the panel and their words.
 */

/** The press, on the case and in the dialog. */
export const ALLOW_ONE_MORE_TRY = 'Allow one more try';

/** Whether a reason is one the platform takes — asked of the press's own contract, not counted again. */
export function oneMoreTryReasonReady(reason: string): boolean {
  return identityRetryGrantRequest.safeParse({ reason }).success;
}

/**
 * WHAT ALLOWING ONE MORE TRY MEANS, said in the dialog before the press — true for this player, whose
 * refused checks so far are `refusalsSoFar`.
 *
 * A refusal of the new check is one more refused check. With three or more in all the case closes
 * again whatever the reason; with fewer, a reason the player can put right leaves them tries of their
 * own, by the rule every refusal is read by (`identityTriesLeft`), and any other reason closes it.
 */
export function whatOneMoreTryMeans(refusalsSoFar: number): string[] {
  const next = refusalsSoFar + 1;
  const leftAfterAFixableRefusal = identityTriesLeft({
    refusals: next,
    latestRetry: true,
    grantSinceLatestRefusal: false,
  });
  return [
    'The player may send new photos once more: their identity page opens the form again.',
    'The new check always goes to a person, whatever the identity service says.',
    leftAfterAFixableRefusal > 0
      ? 'If it is refused for a reason the player can put right, they may send new photos again ' +
        `(that refusal would be ${next} of ${IDENTITY_TRIES} tries); for any other reason, the case ` +
        'closes again.'
      : 'If it is refused, the case closes again, whatever the reason.',
    'Allowing it cannot be undone. The player is emailed that they may send new photos once more, if ' +
      'we can write to their address.',
  ];
}

/**
 * WHAT THE PERSON WHO PRESSED IS TOLD AFTERWARDS, from the press's own answer: whether a message to the
 * player was queued (none for an account with no address, nor one the mailer will not write to).
 */
export function oneMoreTryToast(playerRef: string, emailed: boolean): string {
  return emailed
    ? `${playerRef} may send new photos once more, and has been emailed to say so. A person decides ` +
        'the new check.'
    : `${playerRef} may send new photos once more. No email could be sent to them, so tell them ` +
        'yourself. A person decides the new check.';
}

/**
 * WHY THE PLAYER CANNOT TRY AGAIN ON THEIR OWN, beside the press on the case. The case route draws the
 * press only on a closed case, so it is one of two things: three refused checks or more, or fewer
 * with a reason the player cannot put right.
 */
export function whyTheCaseIsClosed(refusalsSoFar: number): string {
  return refusalsSoFar >= IDENTITY_TRIES
    ? `They have had ${refusalsSoFar} refused checks, so they cannot send documents again on their own.`
    : 'The reason given is one the player cannot put right, so they cannot send documents again on ' +
        'their own.';
}

/**
 * THE DIALOG, for one case. It cannot be dismissed — a reason typed for this player is the only
 * account of this decision anybody will have — and Cancel is its one way out. Its box is its own
 * state, mounted once per case (`key` at the call), so a reason typed for one player is never sent
 * for another; its title names the player, because the case behind a dialog is blurred past reading.
 */
export function AllowOneMoreTryDialog({
  c,
  open,
  busy,
  onClose,
  onAllow,
}: {
  c: { playerRef: string; refusalsSoFar: number };
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onAllow: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const ready = oneMoreTryReasonReady(reason);
  const tooShort =
    reason.length > 0 && reason.trim().length < AUDIT_REASON_MIN_LENGTH
      ? ONE_MORE_TRY_REASON_TOO_SHORT
      : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Allow ${c.playerRef} one more try`}
      dismissible={false}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => onAllow(reason.trim())} disabled={busy || !ready}>
            {ALLOW_ONE_MORE_TRY}
          </Button>
        </>
      }
    >
      {/* WARN, NOT DANGER: it moves no money and takes nothing away, and it cannot be undone. */}
      <div className="notice notice-warn">
        <Icon name="warning" />
        <div>
          {whatOneMoreTryMeans(c.refusalsSoFar).map((sentence) => (
            <p key={sentence} style={{ margin: 0 }}>
              {sentence}
            </p>
          ))}
        </div>
      </div>
      <Field
        label="Why this player may try once more"
        hint={`At least ${AUDIT_REASON_MIN_LENGTH} characters, kept on the audit trail with your name. The player never sees it.`}
        error={tooShort}
      >
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            minLength={AUDIT_REASON_MIN_LENGTH}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="The player showed their passport to support on a video call"
          />
        )}
      </Field>
    </Dialog>
  );
}

/** ONE MORE TRY, ONCE ALLOWED, on the refusal it answers: who allowed it, when, and why. */
export function OneMoreTryAllowed({ allowed }: { allowed: IdentityAllowedTry }) {
  return (
    <div className="notice notice-quiet" style={{ alignItems: 'flex-start' }}>
      <Icon name="clock-counter-clockwise" />
      <div>
        <strong>One more try allowed</strong>
        <div>
          Allowed by {allowed.by ?? 'a member of staff whose name is no longer on record'} on{' '}
          {momentWords(allowed.at)}.
        </div>
        <div className="text-muted" style={{ overflowWrap: 'anywhere' }}>
          Reason: {allowed.reason}
        </div>
      </div>
    </div>
  );
}
